import { QI_BENCHMARKS } from '../../award-data/qi';
import { qiCriterionFields } from './criteria';
import { QI_FIELDS, type QiGroup } from '../manifest';
import { formatQiXlsxDate } from '../validate';
import { qiExportFieldLabel, QI_EXPORT_GROUP_LABELS } from './labels';
import type { QiProvenance, QiValue } from '../types';
import type { QiExportTable, QiLockedExportVersion, QiPublishedComparison } from './types';

// GPMS User Guide June 2026 §5.2: targets first, then the twelve screen groups.
export const QI_GPMS_SCREEN_ORDER: readonly QiGroup[] = [
  'QIT',
  'PI',
  'PR',
  'UPWL',
  'FMI',
  'MM',
  'ADL',
  'IAD',
  'HP',
  'WF',
  'CEI',
  'QOL',
  'AH',
];
export const QI_COMPARISON_LABEL = 'published comparison, not a target';
const column = (key: string, label: string) => ({ key, label, type: 'string' as const });
export const QI_WORKSHEET_COLUMNS = [
  column('home', 'GPMS home identifier'),
  column('period', 'Reporting period'),
  column('group', 'Screen'),
  column('field', 'Field'),
  column('description', 'Description'),
  column('value', 'Value'),
  column('state', 'Collection state'),
];
export const QI_EVIDENCE_COLUMNS = [
  column('group', 'Group'),
  column('record', 'Record'),
  column('detail', 'Detail'),
  column('value', 'Value'),
];
export const QI_SUMMARY_COLUMNS = [
  column('home', 'GPMS home identifier'),
  column('period', 'Reporting period'),
  column('field', 'Field'),
  column('value', 'Value'),
  column('state', 'Collection state'),
  column('comparison', 'Published comparison'),
  column('source', 'Comparison source'),
];

/** Free text may contain names, identifiers or clinical narrative. Suppress it
 * entirely in aggregate outputs rather than promise a regex can remove names.
 */
export function qiRedactedComment(value: QiValue | undefined): string {
  return value?.state === 'measured' && String(value.value).trim()
    ? 'Comment withheld; consult the authorised source record'
    : '';
}
export function qiExportValue(
  code: string,
  value: QiValue | undefined,
  redactComment = true,
): string | number {
  if (!value || value.state === 'not_collected') return 'Not collected';
  const field = QI_FIELDS.find((item) => item.code === code);
  if (field?.kind === 'comment') return redactComment ? qiRedactedComment(value) : value.value;
  return field?.kind === 'date' ? formatQiXlsxDate(String(value.value)) : value.value;
}
function periodLabel(version: QiLockedExportVersion) {
  const { financial_year: year, quarter } = version.inputs.period;
  return `Q${quarter} ${year}/${String((year + 1) % 100).padStart(2, '0')}`;
}
export function buildQiManualWorksheet(versions: readonly QiLockedExportVersion[]): QiExportTable {
  return {
    columns: QI_WORKSHEET_COLUMNS,
    rows: versions.flatMap((version) =>
      QI_GPMS_SCREEN_ORDER.flatMap((group) =>
        QI_FIELDS.filter((field) => field.group === group).map((field) => {
          const value = version.manifest_values[field.code];
          return {
            version_id: version.id,
            home: version.inputs.home_profile?.service_identifier ?? 'Not recorded',
            period: periodLabel(version),
            group: QI_EXPORT_GROUP_LABELS[group],
            field: field.code,
            description: qiExportFieldLabel(field.code),
            value:
              group === 'QIT' && value?.state !== 'measured'
                ? 'Not set'
                : qiExportValue(field.code, value, false),
            state:
              value?.state === 'measured'
                ? 'Measured'
                : group === 'QIT'
                  ? 'Not set'
                  : 'Not collected',
          };
        }),
      ),
    ),
  };
}

export function buildQiEvidenceRegister(
  version: QiLockedExportVersion,
  context: { timezone: string; formatEnum: (value: string) => string; actorRole?: string },
  groupCode?: QiGroup,
): QiExportTable {
  const rows: Record<string, unknown>[] = [];
  const cohort = [
    'QUALITY_OFFICER',
    'COMPLIANCE_OFFICER',
    'FACILITY_MANAGER',
    'TENANT_ADMIN',
  ].includes(context.actorRole ?? '');
  const add = (group: QiGroup, record: string, detail: string, value: unknown) =>
    rows.push({
      version_id: version.id,
      group: `${version.inputs.home_profile?.service_identifier ?? 'Home not recorded'} / ${periodLabel(version)} / ${QI_EXPORT_GROUP_LABELS[group] ?? group}`,
      record,
      detail,
      value: value == null ? 'Not recorded' : String(value),
    });
  const instant = (value: string | null | undefined) =>
    value ? new Date(value).toLocaleString('en-AU', { timeZone: context.timezone }) : undefined;
  const provenance = (group: QiGroup, record: string, p: QiProvenance, aggregate = true) => {
    add(group, record, 'Source kind', context.formatEnum(p.kind));
    add(
      group,
      record,
      'Conformance',
      p.conformance === 'unknown'
        ? p.override_reason || version.policy_override_reason
          ? 'Unknown with attestation'
          : 'Unknown (attestation not recorded)'
        : p.conformance === 'non_conforming'
          ? 'Non-conforming'
          : 'Conforming',
    );
    add(
      group,
      record,
      'Confirmed by',
      !aggregate && cohort
        ? p.confirmed_by
        : p.confirmed_by
          ? 'Recorded (identifier withheld)'
          : null,
    );
    add(group, record, 'Confirmed at', instant(p.confirmed_at));
    // Free-text attestations are withheld in every evidence mode. The authorised
    // subject register retains source lineage under the same D10 read.
    add(
      group,
      record,
      'Source references',
      aggregate
        ? p.source_refs.length
          ? `${p.source_refs.length} reference(s); consult source record`
          : 'None'
        : p.source_refs.join('; ') || 'None',
    );
    add(
      group,
      record,
      'Attestation reason',
      p.override_reason
        ? 'Recorded; consult source record'
        : version.policy_override_reason
          ? 'Recorded at version lock; consult source record'
          : 'Not recorded',
    );
  };
  for (const group of version.inputs.groups.filter(
    (g) => !groupCode || g.group_code === groupCode,
  )) {
    const code = group.group_code;
    const includeIdentifiers = group.mode === 'SUBJECT' && cohort;
    add(code, 'Quarter', 'Period', periodLabel(version));
    add(code, 'Quarter', 'Locked version', version.version_number);
    add(code, 'Quarter', 'Version reference', version.id);
    add(
      code,
      'Quarter',
      'Locked by',
      includeIdentifiers
        ? version.locked_by
        : version.locked_by
          ? 'Recorded (identifier withheld)'
          : null,
    );
    add(code, 'Quarter', 'Locked at', instant(version.locked_at));
    add(
      code,
      'Quarter',
      'Policy override',
      version.policy_override_reason ? 'Recorded; consult source record' : 'Not recorded',
    );
    add(code, 'Quarter', 'Inputs SHA256', version.inputs_hash);
    add(code, 'Collection', 'Mode', group.mode === 'SUBJECT' ? 'Subject' : 'Aggregate');
    add(
      code,
      'Collection',
      'Status',
      group.status === 'COMPLETE'
        ? 'Complete'
        : group.status === 'COLLECTING'
          ? 'Collecting'
          : 'Not collected',
    );
    for (const [key, value] of Object.entries(group.collection_metadata))
      add(
        code,
        'Collection',
        context.formatEnum(key),
        key === 'reviewer_user_id'
          ? includeIdentifiers
            ? value
            : 'Recorded (identifier withheld)'
          : formatQiXlsxDate(value),
      );
    // Aggregate files never contain retained per-person rows, baselines or
    // criterion facts. Subject-mode identifiers require the verified export cohort.
    if (group.mode === 'SUBJECT') {
      const subjects = version.inputs.subjects.filter((subject) => subject.group_id === group.id);
      subjects.forEach((subject, index) => {
        const record = includeIdentifiers
          ? `${context.formatEnum(subject.subject_kind)}: ${subject.subject_id} / ${context.formatEnum(subject.subgroup)}`
          : `Subject ${index + 1}`;
        add(code, record, 'Input usage', 'Used by subject mode');
        for (const field of qiCriterionFields(code, subject.criterion, context.formatEnum))
          add(code, record, field.label, field.value);
        add(
          code,
          record,
          'Primary exclusion',
          subject.primary_exclusion_reason
            ? context.formatEnum(subject.primary_exclusion_reason)
            : null,
        );
        add(
          code,
          record,
          'Comment',
          subject.comment ? 'Free text withheld; consult source record' : 'Not recorded',
        );
        provenance(code, record, subject.provenance, !includeIdentifiers);
      });
    } else
      add(
        code,
        'Retained sources',
        'Subject source count (not exported)',
        version.inputs.subjects.filter((subject) => subject.group_id === group.id).length,
      );
    for (const aggregate of version.inputs.aggregates.filter((a) => a.group_id === group.id)) {
      add(
        code,
        aggregate.code,
        'Input usage',
        group.mode === 'AGGREGATE' ? 'Used by aggregate mode' : 'Retained alternate-mode source',
      );
      add(code, aggregate.code, 'Value', qiExportValue(aggregate.code, aggregate.value));
      add(code, aggregate.code, 'Evidence reference', aggregate.evidence_id);
      provenance(code, aggregate.code, aggregate.provenance);
    }
    for (const baseline of (group.mode === 'SUBJECT' ? version.inputs.baselines : []).filter((b) =>
      code === 'WF'
        ? b.previous_staff_hours != null
        : code === 'ADL'
          ? b.previous_adl_total != null
          : code === 'UPWL' && b.previous_finishing_weight != null,
    )) {
      const record = includeIdentifiers
        ? `Baseline: ${baseline.subject_id} / ${context.formatEnum(baseline.subgroup)}`
        : 'Subject baseline';
      add(
        code,
        record,
        'Previous value',
        code === 'WF'
          ? baseline.previous_staff_hours
          : code === 'ADL'
            ? baseline.previous_adl_total
            : baseline.previous_finishing_weight,
      );
      provenance(code, record, baseline.provenance, !includeIdentifiers);
    }
    for (const evidence of version.inputs.evidence.filter(
      (e) => e.group_id === group.id || e.group_id === null,
    )) {
      add(code, evidence.id, 'Evidence SHA256', evidence.sha256);
      add(code, evidence.id, 'Received at', instant(evidence.received_at));
      add(code, evidence.id, 'Made at', instant(evidence.made_at));
      add(code, evidence.id, 'Retain until', instant(evidence.retention_until));
      add(code, evidence.id, 'Legal hold', evidence.legal_hold ? 'Yes' : 'No');
    }
  }
  return { columns: QI_EVIDENCE_COLUMNS, rows };
}

/** Typed comparison hook. Only supply independently verified, matching-unit
 * AIHW observations. Missing comparison is explicitly unavailable, never zero.
 */
export function buildQiQuarterlySummary(
  versions: readonly QiLockedExportVersion[],
  comparisons: readonly QiPublishedComparison[] = [],
): QiExportTable {
  return {
    columns: QI_SUMMARY_COLUMNS,
    rows: [
      ...versions.flatMap((version) =>
        QI_FIELDS.filter((field) => field.group !== 'QIT').map((field) => {
          const value = version.manifest_values[field.code];
          const comparison = comparisons.find(
            (c) => c.fieldCode === field.code && c.unit === 'count' && field.kind === 'count',
          );
          return {
            version_id: version.id,
            home: version.inputs.home_profile?.service_identifier ?? 'Not recorded',
            period: periodLabel(version),
            field: field.code,
            value: qiExportValue(field.code, value),
            state: value?.state === 'measured' ? 'Measured' : 'Not collected',
            comparison: comparison
              ? `${comparison.value} (${comparison.period}); ${QI_COMPARISON_LABEL}`
              : `Unavailable; ${QI_COMPARISON_LABEL}`,
            source: comparison?.sourceUrl ?? 'No matching field/unit comparison',
          };
        }),
      ),
      ...Object.entries(QI_BENCHMARKS).map(([domain, benchmark]) => ({
        version_id: versions[0]?.id,
        home: 'National publication (separate measure)',
        period: benchmark.quarter,
        field: `AIHW: ${domain.replaceAll('_', ' ')}`,
        value: 'Not a home value',
        state: 'Published context',
        comparison: `${benchmark.national}${benchmark.unit}; ${benchmark.measure ?? domain}; ${QI_COMPARISON_LABEL}`,
        source:
          'AIHW Residential Aged Care Quality Indicators supplementary tables; published February 2026; verified 7 April 2026; https://www.gen-agedcaredata.gov.au/',
      })),
    ],
  };
}
export function buildQiTrendReport(
  versions: readonly QiLockedExportVersion[],
  comparisons: readonly QiPublishedComparison[] = [],
): QiExportTable {
  const latest = new Map<string, QiLockedExportVersion>();
  for (const version of versions) {
    const current = latest.get(version.period_id);
    if (!current || current.version_number < version.version_number)
      latest.set(version.period_id, version);
  }
  return buildQiQuarterlySummary(
    [...latest.values()].sort(
      (a, b) =>
        a.inputs.period.period_start.localeCompare(b.inputs.period.period_start) ||
        a.facility_id.localeCompare(b.facility_id),
    ),
    comparisons,
  );
}
